import type React from 'react';
import { Children, isValidElement, type ReactNode } from 'react';

export const TERMS_LAST_MODIFIED = 'May, 2026';

const paragraphClassName = 'text-foreground';
const sectionClassName = 'space-y-4';
const subsectionClassName = 'pl-4';
const hangingIndentClassName = 'pl-6 -indent-6';
const bodyIndentClassName = 'pl-6';

export const TermsParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={paragraphClassName}>{children}</p>
);

const getParagraphContent = (child: ReactNode): ReactNode => {
  if (isValidElement<{ children?: ReactNode }>(child) && child.type === TermsParagraph) {
    return child.props.children;
  }

  return child;
};

const getRestChildKey = (child: ReactNode, label: string): string => {
  if (isValidElement(child) && child.key != null) {
    return String(child.key);
  }

  return `${label}-${String(child)}`;
};

const noticeClassName = `${paragraphClassName} font-semibold uppercase`;

interface TermsNoticeProps {
  children: React.ReactNode;
  block?: boolean;
}

export const TermsNotice: React.FC<TermsNoticeProps> = ({ children, block = false }) => {
  if (block) {
    return <p className={noticeClassName}>{children}</p>;
  }

  return <span className={noticeClassName}>{children}</span>;
};

export const TermsLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    className="text-primary underline underline-offset-2 hover:text-primary/80"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

interface TermsSectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
}

export const TermsSection: React.FC<TermsSectionProps> = ({ number, title, children }) => (
  <section className={sectionClassName}>
    <h2 className="text-xl font-bold text-foreground">
      <strong>
        {number}. {title}
      </strong>
    </h2>
    <div className="space-y-4">{children}</div>
  </section>
);

interface TermsSubsectionProps {
  label: string;
  title: string;
  children: React.ReactNode;
}

export const TermsSubsection: React.FC<TermsSubsectionProps> = ({ label, title, children }) => {
  const childArray = Children.toArray(children);
  const firstContent = childArray.length > 0 ? getParagraphContent(childArray[0]) : null;
  const restChildren = childArray.slice(1);

  return (
    <div className={`space-y-4 ${subsectionClassName}`}>
      <p className={`${paragraphClassName} ${hangingIndentClassName}`}>
        <span className="font-bold">
          {label}.{title ? ` ${title}` : ''}
        </span>
        {firstContent !== null && <> {firstContent}</>}
      </p>

      {restChildren.length > 0 && (
        <div className={`space-y-4 ${bodyIndentClassName}`}>
          {restChildren.map((child) => {
            const key = getRestChildKey(child, label);

            return isValidElement(child) && child.type === TermsParagraph ? (
              <TermsParagraph key={key}>{child.props.children}</TermsParagraph>
            ) : (
              <p key={key} className={paragraphClassName}>
                {child}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const TermsOfUseContent: React.FC = () => {
  return (
    <div className="space-y-8 text-foreground leading-relaxed">
      <TermsParagraph>
        These terms of use are entered into between you and Steerbridge Strategies, LLC
        (“Steerbridge,” “we,” or “us”). The following terms and conditions, together with any
        documents they expressly incorporate by reference (collectively, “Terms” or “Terms of Use”)
        govern your access to and use of https://steerbridge.com (and its affiliates and/or
        subsidiaries), including any content, functionality, and services offered on or through{' '}
        <TermsLink href="https://steerbridge.com"> https://steerbridge.com</TermsLink>
        {''}, including, but not limited to, https://steerfi.steerbridge.com (collectively, the
        “Website”).
      </TermsParagraph>
      <TermsParagraph>
        {' '}
        Please read the Terms of Use carefully before you start to use the Website.{' '}
        <strong>
          By using the Website or by clicking to accept or agree to the Terms of Use when this
          option is made available to you, you accept and agree to be bound and abide by these Terms
          of Use and our Privacy Policy, found at
          <TermsLink href="https://steerbridge.com/privacy-policy">
            {' '}
            https://steerbridge.com/privacy-policy
          </TermsLink>{' '}
          , incorporated herein by reference.
        </strong>
        If you do not want to agree to these Terms of Use or the Privacy Policy, you must not access
        or use the Website.
      </TermsParagraph>
      <TermsParagraph>
        <TermsNotice>
          NO PART OF THE WEBSITE IS INTENDED FOR PERSONS UNDER THE AGE OF 18 OR THE AGE OF MAJORITY
          IN THE APPLICABLE JURISDICTION. IF YOU ARE UNDER 18 YEARS OF AGE OR THE AGE OF MAJORITY IN
          YOUR JURISDICTION, PLEASE DO NOT USE OR ACCESS THE WEBSITE AT ANY TIME OR IN ANY MANNER.
        </TermsNotice>
      </TermsParagraph>
      <TermsParagraph>
        <TermsNotice>
          AS FURTHER DESCRIBED BELOW, THESE TERMS REQUIRE THE USE OF ARBITRATION ON AN INDIVIDUAL
          BASIS TO RESOLVE DISPUTES, RATHER THAN JURY TRIALS OR CLASS ACTIONS, AND ALSO LIMIT THE
          REMEDIES AVAILABLE TO YOU IN THE EVENT OF A DISPUTE.
        </TermsNotice>
      </TermsParagraph>

      <TermsSection number={1} title="General Terms">
        <TermsSubsection label="a" title="Description.">
          <TermsParagraph>
            The Website provides users with background information about Steerbridge’s brands,
            products, and services, and allows users to apply for jobs and contact Steerbridge. The
            Website also provides access to Steerbridge’s SteerFi payment platform, which
            facilitates payment processing services.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="b" title="Subscribing Entities.">
          <TermsParagraph>
            If you wish to use the Website on behalf of a company, entity, or organization (each, a
            “Subscribing Entity”), then you represent and warrant that you: (a) are an authorized
            representative of that Subscribing Entity with the authority to bind such entity to
            these Terms, and (b) agree to be bound by these Terms on behalf of such Subscribing
            Entity. In addition, if you (or any Subscribing Entity you represent) reside or are
            located or based in China, Russia, or any other jurisdiction to which we apply
            Conditional Access Measures as further described in Section 14.b below, you acknowledge
            and agree that you are expressly prohibited from accessing or using the Website in any
            manner, and you must leave the Website immediately.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="c" title="Changes to the Terms of Use.">
          <TermsParagraph>
            Steerbridge reserves the right to revise these Terms in its sole discretion at any time
            and without prior notice to you other than by posting the revised Terms on the Website.
            Revisions to the Terms are effective upon posting. The Terms will be identified as of
            the most recent date of revision. Your continued use of the Website after a revised
            version of these Terms has been posted on the Website by Steerbridge constitutes your
            binding acceptance of the revised Terms. Steerbridge will endeavor to provide you with
            prior notice of any material changes to these Terms by posting them to the Website.
            Notwithstanding the preceding sentences of this Section 1.c, no revisions to these Terms
            will apply to any dispute between you and Steerbridge that arose prior to the date of
            such revision.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="d" title="Evolving Nature of Website">
          <TermsParagraph>
            The Website is continually evolving and changes to the Website may be made at any time.
            We may update the content on this Website from time to time, but its content is not
            necessarily complete or up-to-date. Any of the material on the Website may be out of
            date at any given time, and we are under no obligation to update such material. If you
            do not like the Website or in any way disagree with these Terms, we hope you will let us
            know and give us constructive feedback. Please feel free to contact us at any time by
            e-mail at contact@steerbridge.com. However, your only remedy in the event you are
            dissatisfied with the Website, or these Terms is to discontinue your use of the Website.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="e" title="Consideration">
          <TermsParagraph>
            You understand and agree that these Terms are entered into in consideration of your use
            of the Website and other good and valuable consideration, the receipt and sufficiency of
            which are hereby acknowledged.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="f" title="Consent to Electronic Communications">
          <TermsParagraph>
            By using the Website, you consent to receiving communications from us as further
            described in the Privacy Policy located at https://www.steerbridge.com/privacy-policy
            (“Privacy Policy”). Please read the Privacy Policy to learn more about your choices
            regarding our electronic communications practices. You agree that any notices,
            agreements, disclosures, or other communications that we send to you electronically will
            satisfy any legal communication requirements, including written communications. If you
            no longer want to receive communications, you may change your preferences at any time by
            clicking the link at the bottom of each e-mail communication.
          </TermsParagraph>
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={2} title="Your Access to the Website">
        <TermsSubsection label="a" title="Access Credentials.">
          <TermsParagraph>
            You will be required to enter a username and password to access and use non-public
            portions of the Website. You may not share your access credentials with any third party.
            You will be responsible for all activity that occurs under your access credentials. You
            agree to use reasonable efforts to prevent unauthorized access to or use of the Website
            and to preserve the confidentiality of your access credentials, and any device that you
            use to access the Website.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="b" title="Online Purchases and Other Terms and Conditions.">
          <TermsParagraph>
            All purchases through our site or other transactions for the sale of goods, services or
            information carried out through the Website, or resulting from visits made by you, are
            governed by our Terms of Sale [LINK TO TERMS OF SALE], which are hereby incorporated
            into these Terms of Use. Additional terms and conditions may also apply to specific
            portions, services, or features of the Website. All such additional terms and conditions
            are hereby incorporated by this reference into these Terms of Use.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="c" title="Linking to the Website and Social Media Features.">
          <TermsParagraph>
            You may link to our homepage, provided you do so in a way that is fair and legal and
            does not damage our reputation or take advantage of it, but you must not establish a
            link in such a way as to suggest any form of association, approval, or endorsement on
            our part without our express written consent.
          </TermsParagraph>
          <TermsParagraph>
            This Website may provide certain social media features that enable you to:
          </TermsParagraph>
          <TermsSubsection label="i" title="">
            Link from your own or certain third-party websites to certain content on this Website
          </TermsSubsection>
          <TermsSubsection label="ii" title="">
            Send emails or other communications with certain content, or links to certain content,
            on this Website.
          </TermsSubsection>
          <TermsSubsection label="iii" title="">
            Cause limited portions of content on this Website to be displayed or appear to be
            displayed on your own or certain third-party websites.
          </TermsSubsection>
          <TermsParagraph>
            You may use these features solely as they are provided by us, solely with respect to the
            content they are displayed with, and otherwise in accordance with any additional terms
            and conditions we provide with respect to such features. Subject to the foregoing, you
            must not:
          </TermsParagraph>
          <TermsSubsection label="iv" title="">
            Establish a link from any website that is not owned by you.
          </TermsSubsection>
          <TermsSubsection label="v" title="">
            Cause the Website or portions of it to be displayed on, or appear to be displayed by,
            any other site, for example, framing, deep linking, or in-line linking.
          </TermsSubsection>
          <TermsSubsection label="vi" title="">
            Link to any part of the Website other than the homepage.
          </TermsSubsection>
          <TermsSubsection label="vii" title="">
            Otherwise take any action with respect to the materials on this Website that is
            inconsistent with any other provision of these Terms of Use.
          </TermsSubsection>
          <TermsParagraph>
            You agree to cooperate with us in causing any unauthorized framing or linking
            immediately to stop. We reserve the right to withdraw linking permission without notice.
            We may disable all or any social media features and any links at any time without notice
            in our discretion.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="d" title="Third Party Websites.">
          <TermsParagraph>
            The Website may include links to external websites and applications owned or controlled
            by third parties. You are responsible for evaluating whether you want to access or use
            them. We are not responsible for and do not endorse any features, content, advertising,
            products, or other materials on third party websites or applications. You assume all
            risk and we disclaim all liability arising from your use of them.
          </TermsParagraph>
        </TermsSubsection>

        <TermsSubsection label="e" title="Information Provided to Steerbridge.">
          <TermsParagraph>
            You agree to provide true, accurate, current, and complete information as requested in
            any registration, sign-up, or other form that you submit to Steerbridge through the
            Website, and to update this information promptly, and as necessary, to keep it current
            and accurate.
          </TermsParagraph>
        </TermsSubsection>
        <TermsSubsection label="f" title="Prohibited Uses.">
          <TermsParagraph>
            You may use this Website only for lawful purposes and in accordance with these terms of
            Use. You agree not to use the Website:
          </TermsParagraph>
          <TermsSubsection label="i" title="">
            In any way that violates any applicable federal, state, local, or international law or
            regulation (including, without limitation, any laws regarding the export of data or
            software to and from the US or other countries).
          </TermsSubsection>
          <TermsSubsection label="ii" title="">
            For the purpose of exploiting, harming, or attempting to exploit or harm minors in any
            way by exposing them to inappropriate content, asking for personally identifiable
            information, or otherwise.
          </TermsSubsection>
          <TermsSubsection label="iii" title="">
            To create a false identity or impersonate another person or entity in any way, including
            to impersonate or attempt to impersonate Steerbridge, a Steerbridge employee, another
            user, or any other person or entity (including, without limitation, by using email
            addresses or screen names associated with any of the foregoing).
          </TermsSubsection>
          <TermsSubsection label="iv" title="">
            To restrict, discourage, or inhibit any person from using the Website, disclose personal
            information about a third person obtained from the Website without the consent of such
            person, or collect information about users of the Website not otherwise provided herein.
          </TermsSubsection>
          <TermsSubsection label="v" title="">
            To undertake, cause, permit, or authorize the modification, creation of derivative
            works, translation, reverse engineering, decompiling, disassembling, or hacking of any
            aspect of the Website or any part thereof, or attempt to do any of the foregoing.
          </TermsSubsection>
          <TermsParagraph>Additionally, you agree not to</TermsParagraph>
          <TermsSubsection label="vi" title="">
            Gain unauthorized access to the Website, to other users’ names or personally
            identifiable information, or to other computers or Websites connected or linked to the
            Website.
          </TermsSubsection>
          <TermsSubsection label="vii" title="">
            Reproduce, distribute, publicly display, publicly perform, sell, trade, resell, or
            exploit any portion of the Website, use of the Website, access to the Website or content
            obtained through the Website, for any purpose other than expressly permitted by these
            Terms.
          </TermsSubsection>
          <TermsSubsection label="viii" title="">
            Transmit or make available any virus, worm, spyware, or any other computer code, file,
            or program that may or is intended to damage or hijack the operation of any hardware,
            software, or telecommunications equipment, or any other aspect of the Website or
            communications equipment and computers connected to the Website.
          </TermsSubsection>
          <TermsSubsection label="ix" title="">
            Use any robot, spider, or other automatic device, process, or means to access the
            Website for any purpose, including monitoring or copying any of the material on the
            Website.
          </TermsSubsection>
          <TermsSubsection label="x" title="">
            Remove, disable, damage, circumvent, or otherwise interfere with any security-related
            features of the Website, features that prevent or restrict the use, or copying of any
            part of the Website or any content on the Website, or features that enforce limitations
            on the use of the Website or any content on the Website.
          </TermsSubsection>
          <TermsSubsection label="xi" title="">
            Use any scraper, spider, robot, or other automated means of any kind to access the
            Website, deep-link to any feature or content on the Website, bypass our robot exclusion
            headers, or other measures we may use to prevent or restrict access to the Website.
          </TermsSubsection>
          <TermsSubsection label="xii" title="">
            To attack the Website via a denial-of-service attack or a distributed denial-of-service
            attack.
          </TermsSubsection>
          <TermsSubsection label="xiii" title="">
            Interfere with or disrupt the Website, networks, or servers connected to the Website or
            violate the regulations, policies, or procedures of such networks or servers.
          </TermsSubsection>
          <TermsSubsection label="xiv" title="">
            Submit any Comments to the Website that are obscene, bigoted, racist, abusive, vulgar,
            profane, intended to defame, harass, threaten, or stalk, or that are otherwise
            objectionable or could give rise to legal or civil liability, as determined by
            Steerbridge in its sole discretion.
          </TermsSubsection>
          <TermsSubsection label="xv" title="">
            Assist or permit any persons in engaging in any of the activities described above.
          </TermsSubsection>
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={3} title="Website Security">
        <TermsParagraph>
          You are prohibited from violating, or attempting to violate, the security of the Website.
          Any such violations may result in criminal and/or civil penalties against you, as well as
          the termination of your privilege to use the Website, at Steerbridge’s sole discretion.
          Steerbridge reserves the right to investigate any alleged or suspected violations and, if
          a criminal violation is suspected, refer such suspected violation to the appropriate law
          enforcement agencies and cooperate fully with such investigations, including, but not
          limited to, the disclosure of any or all of your activities on or related to the Website.
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={4} title="Intellectual Property Rights">
        <TermsSubsection label="a" title="Generally.">
          The content made available on or through the Website, including without limitation, any
          text, graphics, photos, software, music, videos, sounds, and interactive features, may be
          protected by copyright or other intellectual property rights, and is owned by Steerbridge
          or third-party licensors of Steerbridge (collectively, “Steerbridge Content”). You may not
          copy, reproduce, upload, republish, transmit, post, or distribute any Steerbridge Content,
          or any other materials from the Website in any way without prior express written
          permission of Steerbridge and/or the applicable copyright owner of such material or as
          otherwise specified in these Terms or permitted by functionalities of the Website. You may
          not modify any materials obtained from the Website unless you have obtained prior express
          written authorization from Steerbridge and/or the applicable copyright owner. Steerbridge
          solely owns all design rights, databases, compilations and other intellectual property
          rights in and to the Website, in each case whether registered or unregistered, and all
          related goodwill.
        </TermsSubsection>
        <TermsSubsection label="b" title="Trademarks.">
          All trademarks, service marks, logos, and trade names on the Website, whether registered
          or unregistered, are proprietary to Steerbridge or to other companies where so indicated.
          You may not reproduce, download, or otherwise use any such trademarks, service marks,
          logos, or trade names without the prior express written consent of Steerbridge or the
          appropriate owner thereof.
        </TermsSubsection>
        <TermsSubsection label="c" title="License Grant to You.">
          You are hereby granted a limited, non-assignable, revocable right to access and use the
          Website for your personal or commercial use as expressly authorized by Steerbridge,
          including any software included with the Website, as provided for in these Terms and
          pursuant to the authorized features of the Website without modification, so long as you
          agree to these Terms, subject to Steerbridge’s right to terminate your right to use the
          Website in its sole discretion at any time.
        </TermsSubsection>
        <TermsSubsection label="d" title="No Implied Rights.">
          There are no implied licenses granted in these Terms and all rights not granted to you as
          a user are expressly reserved by Steerbridge.
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={5} title="Feedback">
        <TermsParagraph>
          We appreciate hearing from our users and welcome your comments regarding the Website.
          Please be advised, however, that if you send us creative ideas, suggestions, inventions,
          or materials (collectively, “Feedback”): (a) you hereby grant to us a worldwide,
          non-exclusive, irrevocable, perpetual, royalty-free, and unlimited license to use the
          Feedback, whether written or oral, in any manner whatsoever; (b) we will not be subject to
          any obligation of confidentiality and will not be liable for any use or disclosure of any
          Feedback; and (c) we will be entitled to unrestricted use of the Feedback for any purpose
          whatsoever, commercial or otherwise, without compensation to you or any other person.
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={6} title="User-Generated Content">
        <TermsSubsection label="a" title="User Comments.">
          To the extent you are permitted to submit comments on the Website (“Comments”),
          Steerbridge will pre-screen Comments and reserves the right (but has no obligation) in its
          sole discretion to refuse or delete any Comments. Steerbridge does not guarantee the
          accuracy, integrity, or quality of any Comments, and under no circumstances will
          Steerbridge be liable in any way for any Comments.
        </TermsSubsection>
        <TermsSubsection label="b" title="License to Comments.">
          You retain ownership of any rights you may have in your Comments. By submitting your
          Comments to the Website, (i) you do not transfer ownership of your rights therein to
          Steerbridge; and (ii) you do hereby irrevocably grant to Steerbridge a perpetual,
          irrevocable, transferable, sublicensable, royalty free, and worldwide right and license to
          use, reproduce, distribute, create derivative works of, publicly display and perform your
          Comments for any purpose and in accordance with applicable law.
        </TermsSubsection>
        <TermsSubsection label="c" title="Monitoring and Enforcement.">
          We have the right to:
          <TermsSubsection label="i" title="">
            Implement content standards at any time that shall apply to any and all Comments.
          </TermsSubsection>
          <TermsSubsection label="ii" title="">
            Remove or refuse to post any Comments for any or no reason in our sole discretion.
          </TermsSubsection>
          <TermsSubsection label="iii" title="">
            Take any action with respect to any Comments that we deem necessary or appropriate in
            our sole discretion, including if we believe that such Comment violates the Terms of
            Use, infringes any intellectual property right or other right of any person or entity,
            threatens the personal safety of users of the Website or the public, or could create
            liability for Steerbridge.
          </TermsSubsection>
          <TermsSubsection label="iv" title="">
            Disclose your identity or other information about you to any third party who claims that
            material posted by you violates their rights, including their intellectual property
            rights or their right to privacy.
          </TermsSubsection>
          <TermsSubsection label="v" title="">
            Take appropriate legal action, including without limitation, referral to law
            enforcement, for any illegal or unauthorized use of the Website.
          </TermsSubsection>
          <TermsSubsection label="vi" title="">
            Terminate or suspend your access to all or part of the Website for any or no reason,
            including without limitation, any violation of these Terms of Use.
          </TermsSubsection>
        </TermsSubsection>
        <TermsSubsection label="d" title="">
          <TermsParagraph>
            Without limiting the foregoing, we have the right to cooperate fully with any law
            enforcement authorities or court order requesting or directing us to disclose the
            identity or other information of anyone posting any materials on or through the Website.{' '}
            <TermsNotice>
              YOU WAIVE AND HOLD HARMLESS STEERBRIDGE AND ITS AFFILIATES, LICENSEES, AND SERVICE
              PROVIDERS FROM ANY CLAIMS RESULTING FROM ANY ACTION TAKEN BY STEERBRIDGE DURING, OR
              TAKEN AS A CONSEQUENCE OF, INVESTIGATIONS BY EITHER STEERBRIDGE OR LAW ENFORCEMENT
              AUTHORITIES.
            </TermsNotice>
          </TermsParagraph>
        </TermsSubsection>
        <TermsSubsection label="e" title="">
          However, we cannot and do not undertake to review all material before it is posted on the
          Website and cannot ensure prompt removal of objectionable material after it has been
          posted. Accordingly, we assume no liability for any action or inaction regarding
          transmissions, communications, or content provided by any user or third party. We have no
          liability or responsibility to anyone for performance or nonperformance of the activities
          described in this section.
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={7} title="Copyright Infringement">
        <TermsParagraph>
          The Digital Millennium Copyright Act (the “DMCA”) provides recourse for copyright owners
          who believe that material appearing online infringes their rights under U.S. law. If you
          believe in good faith that materials posted on the Website infringe your copyrights, you
          (or your agent) may send Steerbridge a “Notification of Claimed Infringement” requesting
          that the material be removed, or access to it blocked. The notice must include the
          following information:
        </TermsParagraph>
        <TermsSubsection label="a" title="">
          A physical or electronic signature of a person authorized to act on behalf of the owner of
          the works that have been allegedly infringed.
        </TermsSubsection>
        <TermsSubsection label="b" title="">
          Identification of the copyrighted work alleged to have been infringed (or if multiple
          copyrighted works located on the Website are covered by a single notification, a
          representative list of such works).
        </TermsSubsection>
        <TermsSubsection label="c" title="">
          Identification of the specific material alleged to be infringing or the subject of
          infringing activity, and information reasonably sufficient to allow Steerbridge to locate
          the material on the Website.
        </TermsSubsection>
        <TermsSubsection label="d" title="">
          Your name, address, telephone number, and e-mail address (if available).
        </TermsSubsection>
        <TermsSubsection label="e" title="">
          A statement that you have a good faith belief that use of the material in the manner
          complained of is not authorized by the copyright owner, its agent, or the law.
        </TermsSubsection>
        <TermsSubsection label="f" title="">
          A statement that the information in the notification is accurate, and under penalty of
          perjury, that the complaining party is authorized to act on behalf of the owner of an
          exclusive right that is allegedly infringed.
        </TermsSubsection>
        <TermsParagraph>
          If you believe in good faith that a notice of copyright infringement has been wrongly
          filed against you, the DMCA permits you to send Steerbridge a counter-notice.
        </TermsParagraph>
        <TermsParagraph>
          Notices and counter-notices must meet the then-current statutory requirements imposed by
          the DMCA; see{' '}
          <TermsLink href="https://www.loc.gov/copyright/">
            https://www.loc.gov/copyright/
          </TermsLink>{' '}
          for details. Notices and counter-notices with respect to the Website should be sent to
          Steerbridge Strategies, LLC, Attention: Legal, 8521 Leesburg Pike, Vienna, VA 22182.
          Consult your legal advisor and see 17 U.S.C. § 512 before filing a notice or
          counter-notice as there are penalties for false claims under the DMCA.
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={8} title="Reliance on Information Posted">
        <TermsParagraph>
          The information presented on or through the Website is made available solely for general
          information purposes. We do not warrant the accuracy, completeness, or usefulness of this
          information. Any reliance you place on such information is strictly at your own risk. We
          disclaim all liability and responsibility arising from any reliance placed on such
          materials by you or any other visitor to the Website, or by anyone who may be informed of
          any of its contents.
        </TermsParagraph>
        <TermsParagraph>
          This Website may include/includes content provided by third parties, including materials
          provided by other users, bloggers, and third-party licensors, syndicators, aggregators,
          and/or reporting services. All statements and/or opinions expressed in these materials,
          and all articles and responses to questions and other content, other than the content
          provided by Steerbridge, are solely the opinions and the responsibility of the person or
          entity providing those materials. These materials do not necessarily reflect the opinion
          of Steerbridge. We are not responsible, or liable to you or any third party, for the
          content or accuracy of any materials provided by any third parties.
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={9} title="Warranty Disclaimers">
        <TermsParagraph>
          <TermsNotice>
            YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT USE OF THE WEBSITE IS AT YOUR SOLE RISK AND
            THAT THE ENTIRE RISK AS TO SATISFACTORY QUALITY, PERFORMANCE, ACCURACY, AND EFFORT IS
            WITH YOU. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE WEBSITE IS PROVIDED “AS
            IS” AND “AS AVAILABLE,” WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND, AND
            STEERBRIDGE HEREBY DISCLAIMS ALL WARRANTIES AND CONDITIONS WITH RESPECT TO THE WEBSITE
            AND ANY SERVICES OFFERED BY STEERBRIDGE, EITHER EXPRESS, IMPLIED, OR STATUTORY,
            INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES AND/OR CONDITIONS OF
            MERCHANTABILITY, OF SATISFACTORY QUALITY, OF FITNESS FOR A PARTICULAR PURPOSE, OF
            ACCURACY, OF QUIET ENJOYMENT, TITLE, AND NON-INFRINGEMENT OF THIRD PARTY RIGHTS.
          </TermsNotice>
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={10} title="Indemnity">
        <TermsParagraph>
          You agree to indemnify and hold Steerbridge, and its officers, directors, employees,
          agents, successors, and assigns harmless from and against any claims, liabilities,
          damages, losses, and expenses, including, without limitation, reasonable legal and
          accounting fees, arising out of or in any way connected to (a) your access, use, or misuse
          of the Website, or Steerbridge Content, or (b) your violation of these Terms. Steerbridge
          will use reasonable efforts to notify you of any such claim, action or proceeding for
          which it seeks an indemnification from you upon becoming aware of it, but if Steerbridge
          is unable to communicate with you in a timely manner because of an inactive e-mail address
          for you, your indemnification obligation will continue notwithstanding Steerbridge’s
          inability to contact you in a timely manner.
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={11} title="Limitation of Liability">
        <TermsParagraph>
          <TermsNotice>
            TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, IN NO EVENT WILL STEERBRIDGE OR
            ITS OFFICERS, DIRECTORS, LICENSORS, EMPLOYEES, SUPPLIERS, SUCCESSORS, OR ASSIGNS BE
            LIABLE TO ANY PARTY FOR ANY DIRECT, INDIRECT, SPECIAL, OR OTHER CONSEQUENTIAL DAMAGES
            ARISING OUT OF OR IN CONNECTION WITH THE USE OR ACCESS OF OR INABILITY TO USE OR ACCESS
            THE WEBSITE OR ANY SERVICES OR CONTENT MADE AVAILABLE THROUGH THE WEBSITE, INCLUDING,
            WITHOUT LIMITATION, ANY LOST PROFITS, BUSINESS INTERRUPTION, OR OTHERWISE, WHETHER BASED
            IN TORT, CONTRACT, OR OTHER LEGAL THEORY, EVEN IF STEERBRIDGE IS EXPRESSLY ADVISED OF
            THE POSSIBILITY OF SUCH DAMAGES. TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW,
            IN NO EVENT SHALL STEERBRIDGE BE LIABLE IN THE AGGREGATE FOR ANY DAMAGES INCURRED BY YOU
            THAT EXCEED ONE HUNDRED DOLLARS.
          </TermsNotice>
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={12} title="Basis of the Bargain">
        <TermsParagraph>
          <TermsNotice>
            APPLICABLE LAW MAY NOT ALLOW THE LIMITATION OR EXCLUSION OF LIABILITY OR INCIDENTAL OR
            CONSEQUENTIAL DAMAGES, SO THE ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU. IN
            SUCH CASES, YOU AGREE THAT BECAUSE SUCH WARRANTY DISCLAIMERS AND LIMITATIONS OF
            LIABILITY REFLECT A REASONABLE AND FAIR ALLOCATION OF RISK BETWEEN YOU AND STEERBRIDGE
            AND ARE FUNDAMENTAL ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN YOU AND STEERBRIDGE IN
            LIGHT OF STEERBRIDGE’S OFFERING MANY OF THE FUNCTIONALITIES OF THE WEBSITE FOR FREE,
            STEERBRIDGE’S LIABILITY WILL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
          </TermsNotice>
        </TermsParagraph>
      </TermsSection>

      <TermsSection number={13} title="Term and Termination">
        <TermsSubsection label="a" title="Term.">
          These Terms will be effective commencing with your first use of the Website and will
          remain in full force and effect throughout your use of the Website, until such time as you
          cease all use of the Website, subject to the Survival provision of these Terms.
        </TermsSubsection>
        <TermsSubsection label="b" title="Termination.">
          Steerbridge may terminate your use of the Website or any features or functionalities of
          the Website at any time and for any reason, with or without notice, for conduct violating
          these Terms or upon Steerbridge’s sole determination. You hereby agree to Steerbridge’s
          broad right of termination. You agree that if your use of the Website is terminated
          pursuant to these Terms, you will not attempt to use the Website, and further agree that
          if you violate this restriction after being terminated, you will indemnify and hold us
          harmless from any and all liability that we may incur therefor. You are free to terminate
          your use of the Website at any time; you can simply choose to stop visiting or using any
          aspect of the Website.
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={14} title="Jurisdictional Matters">
        <TermsSubsection label="a" title="Website Location.">
          The Website is controlled and operated by Steerbridge from its offices within the
          Commonwealth of Virginia. Steerbridge makes no representation that materials on the
          Website or through the Website are appropriate or available for use in other locations.
          Those who choose to access or use the Website from other locations, including from outside
          the United States of America, do so on their own initiative and are responsible for
          compliance with all applicable local laws, ordinances, and regulations, if and to the
          extent that any such local laws, ordinances, and regulations are applicable. Access to the
          Website
        </TermsSubsection>
        <TermsSubsection label="b" title="Conditional Access and Geo-Blocking.">
          Steerbridge reserves the right, at its sole discretion, to control, prohibit and/or limit
          access to the Website by deploying technologies such as geo-blocking software, Internet
          Protocol (IP) address geo-filtering, and/or any other technologies or measures now known
          or that become available in the future that facilitate the restriction of access to our
          Website from locations anywhere outside of the United States of America (“Conditional
          Access Measures”). Such Conditional Access Measures may apply, without limitation, to
          entities, countries, and/or regions set forth on the Department of Commerce’s Commerce
          Control List and the Country Chart Overview, and to any other jurisdiction as determined
          in our sole discretion.
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={15} title="Dispute Resolution">
        <TermsSubsection label="a" title="">
          Steerbridge is not a party to, has no involvement or interest in, makes no representations
          or warranties as to, and has no responsibility or liability with respect to any
          communications, transactions, interactions, disputes, or any relations whatsoever between
          you and any other user of this Website or other third party. Disputes between a user and
          Steerbridge are subject to this Section 15.
        </TermsSubsection>
        <TermsSubsection label="b" title="Mandatory Arbitration.">
          <TermsParagraph>
            Please read this carefully. It affects your rights.
            <TermsNotice>
              YOU AND STEERBRIDGE AND EACH OF OUR RESPECTIVE SUBSIDIARIES, AFFILIATES, PREDECESSORS
              IN INTEREST, SUCCESSORS, AND PERMITTED ASSIGNS AGREE TO ARBITRATION (EXCEPT FOR
              MATTERS THAT MAY BE TAKEN TO SMALL CLAIMS COURT), AS THE EXCLUSIVE FORM OF DISPUTE
              RESOLUTION EXCEPT AS PROVIDED FOR BELOW, FOR ALL DISPUTES AND CLAIMS ARISING OUT OF OR
              RELATING TO THESE TERMS OR YOUR USE OF THE WEBSITE.
            </TermsNotice>
            Arbitration is more informal than a lawsuit in court. Arbitration uses a neutral
            arbitrator instead of a jury or judge, allows for more limited discovery than in court,
            and is subject to very limited review by courts. Arbitrators can award the same damages
            and relief that a court can award. Please visit{' '}
            <TermsLink href="https://www.adr.org">www.adr.org</TermsLink> for more information about
            arbitration.
          </TermsParagraph>
          <TermsSubsection label="i" title="Commencing Arbitration.">
            <TermsParagraph>
              A party who intends to seek arbitration must first send to the other, by certified
              mail, a written notice of intent to arbitrate (a “Notice”), or, in the absence of a
              mailing address provided by you to Steerbridge, to you via any other method available
              to Steerbridge, including via e-mail. The Notice to Steerbridge should be addressed to
              Steerbridge Strategies, LLC, Attention: Legal, 8521 Leesburg Pike, Vienna, VA 22182
              (the “Arbitration Notice Address”). The Notice must (a) describe the nature and basis
              of the claim or dispute; and (b) set forth the specific relief sought (the “Demand”).
              If you and Steerbridge do not reach an agreement to resolve the claim within 30 days
              after the Notice is received, you or Steerbridge may commence an arbitration
              proceeding as set forth below or file a claim in small claims court.
              <TermsNotice>
                THE ARBITRATION SHALL BE ADMINISTERED BY THE AMERICAN ARBITRATION ASSOCIATION
                (“AAA”) IN ACCORDANCE WITH ITS COMMERCIAL ARBITRATION RULES AND THE SUPPLEMENTARY
                PROCEDURES FOR CONSUMER RELATED DISPUTES (the “Rules”), AS MODIFIED BY THIS
                AGREEMENT.
              </TermsNotice>
              Rules and AAA forms are available online at{' '}
              <TermsLink href="https://www.adr.org">www.adr.org</TermsLink>. If you are required to
              pay a filing fee to commence an arbitration against Steerbridge, then Steerbridge will
              promptly reimburse you for your confirmed payment of the filing fee upon Steerbridge’s
              receipt of Notice at the Arbitration Notice Address that you have commenced
              arbitration along with a receipt evidencing payment of the filing fee, unless your
              Demand is equal to or greater than $1,000 or filed in bad faith, in which case you are
              solely responsible for the payment of the filing fee.
            </TermsParagraph>
          </TermsSubsection>
          <TermsSubsection label="ii" title="Arbitration Proceeding">
            The arbitration shall be conducted in the English language. A single independent and
            impartial arbitrator shall be appointed pursuant to the Rules, as modified herein. You
            and Steerbridge agree to comply with the following rules, which are intended to
            streamline the dispute resolution process and reduce the costs and burdens on the
            parties: (a) the arbitration shall be conducted by telephone, online, and/or be solely
            based on written submissions, the specific manner to be chosen by the party initiating
            the arbitration; (b) the arbitration shall not require any personal appearance by the
            parties or witnesses unless otherwise mutually agreed in writing by the parties; and (c)
            any judgment on the award rendered by the arbitrator may be entered in any court of
            competent jurisdiction.
          </TermsSubsection>
          <TermsSubsection label="iii" title="No Class Actions.">
            <TermsNotice>
              YOU AND STEERBRIDGE AGREE THAT YOU AND STEERBRIDGE MAY BRING CLAIMS AGAINST THE OTHER
              ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY
              PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. FURTHER, YOU AGREE THAT THE ARBITRATOR
              MAY NOT CONSOLIDATE PROCEEDINGS OF MORE THAN ONE PERSON’S CLAIMS, AND MAY NOT
              OTHERWISE PRESIDE OVER ANY FORM OF A REPRESENTATIVE OR CLASS PROCEEDING, AND THAT IF
              THIS SPECIFIC PROVISION IS FOUND TO BE UNENFORCEABLE, THEN THE ENTIRETY OF THIS
              SECTION 15.2 SHALL BE NULL AND VOID.
            </TermsNotice>
          </TermsSubsection>
          <TermsSubsection label="iv" title="Decision of the Arbitrator.">
            Barring extraordinary circumstances, the arbitrator shall issue his or her decision
            within 120 days from the date the arbitrator is appointed. The arbitrator may extend
            this time limit for an additional 30 days in the interests of justice. All arbitration
            proceedings shall be closed to the public and confidential and all records relating
            thereto shall be permanently sealed, except as necessary to obtain court confirmation of
            the arbitration award. The award of the arbitrator shall be in writing and shall include
            a statement setting forth the reasons for the disposition of any claim. The arbitrator
            shall apply the laws of the Commonwealth of Virginia in conducting the arbitration. You
            acknowledge that these Terms and your use of the Website evidence a transaction
            involving interstate commerce. The United States Federal Arbitration Act shall govern
            the interpretation, enforcement, and proceedings pursuant to the Mandatory Arbitration
            clause in these Terms.
          </TermsSubsection>
        </TermsSubsection>
        <TermsSubsection label="c" title="Choice of Forum.">
          These Terms and your use of the Website shall be governed by the substantive laws of the
          Commonwealth of Virginia without reference to its choice or conflicts of law principles.
          Only if the Mandatory Arbitration clause is deemed to be null and void, then all disputes
          arising between you and Steerbridge under these Terms shall be subject to the exclusive
          jurisdiction of the state and federal courts located in Fairfax County, Virginia, and you
          and Steerbridge hereby submit to the personal jurisdiction and venue of these courts and
          waive any defense of inconvenient forum.
        </TermsSubsection>
        <TermsSubsection label="d" title="Equitable Relief.">
          The foregoing provisions of this Dispute Resolution Section do not apply to any claim in
          which Steerbridge seeks equitable relief of any kind. You acknowledge that, in the event
          of a breach of these Terms by Steerbridge or any third party, the damage or harm, if any,
          caused to you will not entitle you to seek injunctive or other equitable relief against
          Steerbridge, and your only remedy shall be for monetary damages, subject to the
          limitations of liability set forth in these Terms.
        </TermsSubsection>
        <TermsSubsection label="e" title="Claims.">
          You and Steerbridge agree that, notwithstanding any other rights the party may have under
          law or equity, any cause of action arising out of or related to these Terms or the
          Website, excluding a claim for indemnification, must commence within one year after the
          cause of action accrues. Otherwise, such cause of action is permanently barred.
        </TermsSubsection>
        <TermsSubsection label="f" title="Improperly Filed Claims.">
          All claims you bring against Steerbridge must be resolved in accordance with this Dispute
          Resolution Section. All claims filed or brought contrary to this Dispute Resolution
          Section shall be considered improperly filed. Should you file a claim contrary to this
          Dispute Resolution Section, Steerbridge may recover attorneys’ fees and costs up to
          $5,000, provided that Steerbridge has notified you in writing of the improperly filed
          claim, and you have failed to promptly withdraw the claim.
        </TermsSubsection>
      </TermsSection>

      <TermsSection number={16} title="Miscellaneous">
        <TermsSubsection label="a" title="Entire Agreement.">
          These Terms and the Privacy Policy constitute the entire agreement between you and
          Steerbridge relating to the subject matter herein and supersede all previous and
          contemporaneous communications, representations, understandings, and agreements, either
          oral or written, between you and Steerbridge with respect to your use of the Website.
          These Terms shall not be modified except in writing, signed by both parties, or by a
          change to these Terms made by Steerbridge as authorized in these Terms. If you or the
          entity you represent have entered into a separate written agreement with Steerbridge (a
          "Client Agreement"), the terms of such Client Agreement shall take precedence over these
          Terms of Use solely to the extent of any conflict between the Client Agreement and these
          Terms of Use with respect to the subject matter covered by the Client Agreement. Nothing
          in a Client Agreement shall be construed to limit or waive any of Steerbridge's rights or
          your obligations under these Terms of Use with respect to your use of the Website.
        </TermsSubsection>
        <TermsSubsection label="b" title="Waiver.">
          A provision of these Terms may be waived only by a written instrument executed by the
          party entitled to the benefit of such provision. No failure or delay on the part of
          Steerbridge in the exercise of any power or right under these Terms shall operate as a
          waiver thereof. No single or partial exercise of any right or power under these Terms
          shall operate as a waiver of such right or of any other right or power. The waiver by
          Steerbridge of a breach of any provision of these Terms shall not operate or be construed
          as a waiver of any other or subsequent breach of these Terms.
        </TermsSubsection>
        <TermsSubsection label="c" title="Severability.">
          If any provision of these Terms shall be unlawful, void, or for any reason unenforceable,
          then that provision shall be deemed severable from these Terms and shall not affect the
          validity and enforceability of any remaining provisions.
        </TermsSubsection>
        <TermsSubsection label="d" title="Assignment.">
          These Terms, and any rights and licenses granted hereunder, may not be transferred, or
          assigned by you, but may be assigned by Steerbridge without restriction. Any assignment
          attempted to be made by you in violation of these Terms shall be void. These Terms will be
          binding upon and inure to the benefit of the parties hereto, and permitted successors and
          assigns.
        </TermsSubsection>
        <TermsSubsection label="e" title="No Agency.">
          You agree that no joint venture, partnership, employment, or agency relationship exists
          between you and Steerbridge as a result of these Terms or use of the Website.
        </TermsSubsection>
        <TermsSubsection label="f" title="Survival.">
          The following provisions will survive any termination or expiration of these Terms:
          Sections 1.b (Subscribing Entities), 1.c (Changes to The Terms of Use), 1.e
          (Consideration), 1.f (Consent to Electronic Communications), 4 (Intellectual Property
          Rights), 9 (Warranty Disclaimers), 10 (Indemnity), 11 (Limitation of Liability), 15
          (Dispute Resolution), 16 (Miscellaneous), and any other provision that by its nature is
          intended to survive.
        </TermsSubsection>
        <TermsSubsection label="g" title="Headings.">
          The heading references in these Terms are for convenience purposes only, do not constitute
          a part of these Terms, and shall not be deemed to limit or affect any of the provisions
          hereof.
        </TermsSubsection>
        <TermsSubsection label="h" title="Communication.">
          You can contact Steerbridge any time by e-mail at contact@steerbridge.com, by mail at
          Steerbridge Strategies, LLC, Attention: Legal, 8521 Leesburg Pike, Vienna, VA 22182, or by
          phone at (703) 663-8299.
        </TermsSubsection>
      </TermsSection>
    </div>
  );
};
